import React from 'react'
import { ReactSortable } from 'react-sortablejs'
import DataRepo from '@api/datasource/data'
import { UI } from '@constants/app'
import QueryKeys from '@constants/queryKeys'
import { StatusClientType } from '@customTypes/client'
import { ModalFormType, ModalProps } from '@customTypes/page'
import { useIsMobile } from '@hooks/mobile'
import { ActionIcon, Button, LoadingOverlay, Modal, Paper, Tabs, Text } from '@mantine/core'
import {
  IconBaselineDensityMedium,
  IconCalendarEvent,
  IconCircleFilled,
  IconEdit,
  IconPlus,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { debounceUpdate, isLoadingMutation, isLoadingQuery } from '@utils/network'
import StatusForm from './statusForm'
import ConfirmationModal from '@components/shared/confirmationModal'
import queryClient from '@api/datasource/query'
import { notifications } from '@mantine/notifications'
import { EventTypeType, StatusEventType } from '@customTypes/event'
import EventTypeForm from '@components/event/eventTypeForm'
import EventStatusForm from '@components/event/eventStatusForm'
import Cont from '@components/shared/cont'

const SettingsModal = (props: ModalProps) => {
  const { open, modal, size = 'lg', onClose: outerOnClose } = props

  const clientStatusRef = React.useRef<boolean>(false)

  const eventStatusRef = React.useRef<boolean>(false)

  const [confirmExit, setConfirmExit] = React.useState(false)

  const [statusForm, setModalForm] = React.useState<ModalFormType<StatusClientType>>({
    open: false,
  })

  const [eventTypeForm, setEventTypeForm] = React.useState<ModalFormType<EventTypeType>>({
    open: false,
  })

  const [eventStatusForm, setEventStatusForm] = React.useState<ModalFormType<StatusEventType>>({
    open: false,
  })

  const [statusDelete, setStatusDelete] = React.useState<ModalFormType<StatusClientType>>({
    open: false,
  })

  const [statusEventDelete, setStatusEventDelete] = React.useState<ModalFormType<StatusEventType>>({
    open: false,
  })

  const [eventTypeDelete, setEventTypeDelete] = React.useState<ModalFormType<EventTypeType>>({
    open: false,
  })

  const isMobile = useIsMobile()

  const [statusList, setStatusList] = React.useState<StatusClientType[]>([])

  const [eventStatusList, setEventStatusList] = React.useState<StatusEventType[]>([])

  const statusQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getStatus(),
  })

  const eventTypesQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventTypeOptions(),
  })

  const eventStatusQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getEventStatusOptions(),
  })

  const statusDeleteMutation = useMutation<void, Error, string>({
    mutationFn: (uid) => DataRepo.clientEntityService.deleteStatus(uid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_STATUS_KEY], refetchType: 'all' })
      notifications.show({
        message: 'Estado eliminado correctamente',
        title: 'Éxito',
        color: 'green',
      })
      setStatusDelete({ open: false })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al eliminar el estado',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const eventTypeDeleteMutation = useMutation<void, Error, string>({
    mutationFn: (uid) => DataRepo.clientEntityService.deleteEventType(uid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_TYPES_KEY],
        refetchType: 'all',
      })
      notifications.show({
        message: 'Tipo de evento eliminado correctamente',
        title: 'Éxito',
        color: 'green',
      })
      setEventTypeDelete({ open: false })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al eliminar el tipo de evento',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const eventStatusDeleteMutation = useMutation<void, Error, string>({
    mutationFn: (uid) => DataRepo.clientEntityService.deleteEventStatus(uid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
        refetchType: 'all',
      })
      notifications.show({
        message: 'Estado de evento eliminado correctamente',
        title: 'Éxito',
        color: 'green',
      })
      setStatusEventDelete({ open: false })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al eliminar el estado de evento',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const statusMutation = useMutation<void, Error, StatusClientType[]>({
    mutationFn: (values) => DataRepo.clientEntityService.updateOrderStatus(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_STATUS_KEY], refetchType: 'all' })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al actualizar el orden de estados',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const eventStatusMutation = useMutation<void, Error, StatusEventType[]>({
    mutationFn: (values) => DataRepo.clientEntityService.updateOrderEventStatus(values),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_EVENT_STATUS_KEY],
        refetchType: 'all',
      })
    },
    onError: (error) => {
      notifications.show({
        message: error.message || 'Error al actualizar el orden de estados de eventos',
        title: 'Error',
        color: 'red',
      })
    },
  })

  const statusMemo = React.useMemo(() => statusQuery.data || [], [statusQuery.data])

  const eventStatusMemo = React.useMemo(() => eventStatusQuery.data || [], [eventStatusQuery.data])

  React.useEffect(() => {
    if (statusQuery.isSuccess) {
      setStatusList(statusMemo)
      clientStatusRef.current = true
    }
  }, [open, statusMemo, statusQuery.isSuccess])

  React.useEffect(() => {
    if (eventStatusQuery.isSuccess) {
      setEventStatusList(eventStatusMemo)
      eventStatusRef.current = true
    }
  }, [open, eventStatusMemo, eventStatusQuery.isSuccess])

  const isLoadingStatus = isLoadingQuery(statusQuery)

  const isLoadingEventTypes = isLoadingQuery(eventTypesQuery)

  const isLoadingMutations = isLoadingMutation(statusMutation, eventStatusMutation)

  const content = (
    <Tabs defaultValue="clients" variant="pills">
      <LoadingOverlay
        loaderProps={{ type: 'dots' }}
        overlayProps={{ radius: 'md', blur: 2 }}
        visible={isLoadingStatus || isLoadingEventTypes}
        zIndex={1000}
      />
      <Tabs.List grow>
        <Tabs.Tab
          className="!cd-py-[0.45rem]"
          leftSection={<IconUsers size={20} />}
          value="clients"
        >
          <Text className="!cd-text-lg !cd-leading-2">Clientes</Text>
        </Tabs.Tab>
        <Tabs.Tab
          className="!cd-py-[0.45rem]"
          leftSection={<IconCalendarEvent size={20} />}
          value="events"
        >
          <Text className="!cd-text-lg !cd-leading-2">Eventos</Text>
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel className="cd-flex cd-flex-col cd-gap-4 cd-mt-4" value="clients">
        <div className="cd-flex cd-flex-col md:cd-flex-row md:cd-gap-4 md:cd-items-center md:cd-justify-between">
          <Text className="cd-text-gray-500 dark:cd-text-gray-400">
            Aquí puedes gestionar los estados de los clientes y su orden
          </Text>
          <Button
            color="green"
            leftSection={<IconPlus size={18} />}
            size={UI.Size}
            variant="filled"
            onClick={() => setModalForm({ open: true })}
          >
            Agregar Estado
          </Button>
        </div>

        <ReactSortable
          animation={200}
          className="cd-flex cd-flex-col cd-gap-2"
          delay={2}
          group="groupName"
          list={statusList.map((status) => ({ id: status.uid, name: status.name }))}
          setList={(list) => {
            const updatedStatus = list.map((item, index) => ({
              ...statusQuery.data?.find((status) => status.uid === item.id),
              order: index + 1,
            })) as StatusClientType[]

            if (clientStatusRef.current) {
              setStatusList(updatedStatus)

              debounceUpdate({
                callId: 'update-client-status-order',
                delay: 1000,
                dataFunction: updatedStatus,
                updateFn: (data) => statusMutation.mutate(data),
              })
            }
          }}
        >
          {statusList?.map((status) => (
            <Paper
              withBorder
              className="!cd-p-4 cd-flex cd-flex-col !cd-gap-2 cd-cursor-pointer"
              key={status.uid}
            >
              <div className="cd-flex cd-items-center cd-justify-between cd-gap-4">
                <div className="cd-flex cd-items-center cd-justify-start cd-gap-4">
                  <IconBaselineDensityMedium size={20} />
                  <IconCircleFilled color={status.color} size={28} />
                  <Text>{status.name}</Text>
                </div>

                <div className="cd-flex cd-items-center cd-justify-end cd-gap-6">
                  <ActionIcon
                    variant="transparent"
                    onClick={() => setModalForm({ open: true, data: status })}
                  >
                    <IconEdit size={24} />
                  </ActionIcon>

                  <ActionIcon
                    color="red"
                    variant="transparent"
                    onClick={() => setStatusDelete({ open: true, data: status })}
                  >
                    <IconTrash size={24} />
                  </ActionIcon>
                </div>
              </div>
            </Paper>
          ))}
        </ReactSortable>

        {!statusList?.length && (
          <Text className="cd-text-gray-500 dark:cd-text-gray-400 cd-text-center cd-mt-8 cd-mb-4">
            No hay estados registrados
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel className="cd-flex cd-flex-col cd-gap-4 cd-mt-4" value="events">
        <Tabs defaultValue="eventTypes" variant="outline">
          <Tabs.List grow>
            <Tabs.Tab className="!cd-py-[0.45rem]" value="eventTypes">
              <Text className="!cd-text-base !cd-leading-2">Tipos de Evento</Text>
            </Tabs.Tab>
            <Tabs.Tab className="!cd-py-[0.45rem]" value="eventStatus">
              <Text className="!cd-text-base !cd-leading-2">Estados de Evento</Text>
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel className="cd-flex cd-flex-col cd-gap-4 cd-mt-4" value="eventTypes">
            <div className="cd-flex cd-flex-col md:cd-flex-row md:cd-gap-4 md:cd-items-center md:cd-justify-between">
              <Text className="cd-text-gray-500 dark:cd-text-gray-400">
                Aquí puedes gestionar los tipos de eventos
              </Text>
              <Button
                color="green"
                leftSection={<IconPlus size={18} />}
                size={UI.Size}
                variant="filled"
                onClick={() => setEventTypeForm({ open: true })}
              >
                Agregar Tipo
              </Button>
            </div>

            {Boolean(eventTypesQuery.data?.length) &&
              eventTypesQuery.data?.map((eventType) => (
                <Paper
                  withBorder
                  className="!cd-p-4 cd-flex cd-flex-col !cd-gap-2"
                  key={eventType.uid}
                >
                  <div className="cd-flex cd-items-center cd-justify-between cd-gap-4">
                    <div className="cd-flex cd-items-center cd-justify-start cd-gap-4">
                      <IconCircleFilled color={eventType.color} size={28} />
                      <Text>{eventType.name}</Text>
                    </div>

                    <div className="cd-flex cd-items-center cd-justify-end cd-gap-6">
                      <ActionIcon
                        variant="transparent"
                        onClick={() => setEventTypeForm({ open: true, data: eventType })}
                      >
                        <IconEdit size={24} />
                      </ActionIcon>

                      <ActionIcon
                        color="red"
                        variant="transparent"
                        onClick={() => setEventTypeDelete({ open: true, data: eventType })}
                      >
                        <IconTrash size={24} />
                      </ActionIcon>
                    </div>
                  </div>
                </Paper>
              ))}

            {!eventTypesQuery.data?.length && (
              <Text className="cd-text-gray-500 dark:cd-text-gray-400 cd-text-center cd-mt-8 cd-mb-4">
                No hay tipos de eventos registrados
              </Text>
            )}
          </Tabs.Panel>

          <Tabs.Panel className="cd-flex cd-flex-col cd-gap-4 cd-mt-4" value="eventStatus">
            <div className="cd-flex cd-flex-col md:cd-flex-row md:cd-gap-4 md:cd-items-center md:cd-justify-between">
              <Text className="cd-text-gray-500 dark:cd-text-gray-400">
                Aquí puedes gestionar los estados de los eventos
              </Text>

              <Button
                color="green"
                leftSection={<IconPlus size={18} />}
                size={UI.Size}
                variant="filled"
                onClick={() => setEventStatusForm({ open: true })}
              >
                Agregar Estado
              </Button>
            </div>

            <ReactSortable
              animation={200}
              className="cd-flex cd-flex-col cd-gap-2"
              delay={2}
              group="groupName"
              list={eventStatusList.map((status) => ({
                id: status.uid,
                name: status.name,
              }))}
              setList={(list) => {
                const updatedStatus = list.map((item, index) => ({
                  ...eventStatusQuery.data?.find((status) => status.uid === item.id),
                  order: index + 1,
                })) as StatusEventType[]

                if (eventStatusRef.current) {
                  setEventStatusList(updatedStatus)

                  debounceUpdate({
                    callId: 'update-event-status-order',
                    delay: 1000,
                    dataFunction: updatedStatus,
                    updateFn: (data) => eventStatusMutation.mutate(data),
                  })
                }
              }}
            >
              {eventStatusList?.map((status) => (
                <Paper
                  withBorder
                  className="!cd-p-4 cd-flex cd-flex-col !cd-gap-2 cd-cursor-pointer"
                  key={status.uid}
                >
                  <div className="cd-flex cd-items-center cd-justify-between cd-gap-4">
                    <div className="cd-flex cd-items-center cd-justify-start cd-gap-4">
                      <IconBaselineDensityMedium size={20} />
                      <IconCircleFilled color={status.color} size={28} />
                      <Text>{status.name}</Text>
                    </div>

                    <div className="cd-flex cd-items-center cd-justify-end cd-gap-6">
                      <ActionIcon
                        variant="transparent"
                        onClick={() => setModalForm({ open: true, data: status })}
                      >
                        <IconEdit size={24} />
                      </ActionIcon>

                      <ActionIcon
                        color="red"
                        variant="transparent"
                        onClick={() => setStatusEventDelete({ open: true, data: status })}
                      >
                        <IconTrash size={24} />
                      </ActionIcon>
                    </div>
                  </div>
                </Paper>
              ))}
            </ReactSortable>

            {!eventStatusQuery.data?.length && (
              <Text className="cd-text-gray-500 dark:cd-text-gray-400 cd-text-center cd-mt-8 cd-mb-4">
                No hay estados de eventos registrados
              </Text>
            )}
          </Tabs.Panel>
        </Tabs>
      </Tabs.Panel>
    </Tabs>
  )

  if (modal) {
    return (
      <Modal
        centered
        fullScreen={isMobile}
        opened={Boolean(open)}
        size={size}
        title="Configuración"
        withCloseButton={modal}
        onClose={() => {
          if (isLoadingMutations) {
            notifications.show({
              message: 'Hay cambios pendientes, espera a que se completen',
              title: 'Cargando',
              color: 'yellow',
            })
          } else {
            outerOnClose?.()
          }
        }}
      >
        {content}

        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Eliminar"
          loading={isLoadingMutation(statusDeleteMutation)}
          open={Boolean(statusDelete.open)}
          title="Eliminar Estado"
          onCancel={() => setStatusDelete({ open: false })}
          onConfirm={() => {
            if (!statusDelete.data) return
            statusDeleteMutation.mutate(statusDelete.data.uid)
            setStatusDelete({ open: false })
          }}
        >
          <Text className="cd-text-base">
            ¿Estás seguro que deseas eliminar el estado{' '}
            <Text inherit className="cd-text-base cd-font-bold" component="span">
              {statusDelete.data?.name}
            </Text>
            ?
          </Text>
        </ConfirmationModal>

        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Eliminar"
          loading={isLoadingMutation(eventStatusMutation)}
          open={Boolean(statusEventDelete.open)}
          title="Eliminar Estado de Evento"
          onCancel={() => setStatusEventDelete({ open: false })}
          onConfirm={() => {
            if (!statusEventDelete.data) return

            eventStatusDeleteMutation.mutate(statusEventDelete.data.uid)
          }}
        >
          <Text className="cd-text-base">
            ¿Estás seguro que deseas eliminar el estado de evento{' '}
            <Text inherit className="cd-text-base cd-font-bold" component="span">
              {statusEventDelete.data?.name}
            </Text>
            ?
          </Text>
        </ConfirmationModal>

        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Eliminar"
          loading={isLoadingMutation(eventTypeDeleteMutation)}
          open={Boolean(eventTypeDelete.open)}
          title="Eliminar Tipo de Evento"
          onCancel={() => setEventTypeDelete({ open: false })}
          onConfirm={() => {
            if (!eventTypeDelete.data) return

            eventTypeDeleteMutation.mutate(eventTypeDelete.data.uid)
          }}
        >
          <Text className="cd-text-base">
            ¿Estás seguro que deseas eliminar el tipo de evento{' '}
            <Text inherit className="cd-text-base cd-font-bold" component="span">
              {eventTypeDelete.data?.name}
            </Text>
            ?
          </Text>
        </ConfirmationModal>

        <ConfirmationModal
          cancelColor="gray"
          cancelText="Cancelar"
          confirmColor="red"
          confirmText="Descartar"
          open={confirmExit}
          title="Descartar cambios"
          onCancel={() => setConfirmExit(false)}
          onConfirm={() => {
            setConfirmExit(false)
            outerOnClose?.()
          }}
        >
          <Text className="cd-text-base">
            Has realizado cambios en el orden ¿Deseas descartar los cambios?
          </Text>
        </ConfirmationModal>

        <StatusForm
          modal
          data={statusForm.data}
          open={statusForm.open}
          size="lg"
          onClose={() => setModalForm({ open: false })}
        />

        <EventTypeForm
          modal
          data={eventTypeForm.data}
          open={eventTypeForm.open}
          size="lg"
          onClose={() => setEventTypeForm({ open: false })}
        />

        <EventStatusForm
          modal
          data={eventStatusForm.data}
          open={eventStatusForm.open}
          size="lg"
          onClose={() => setEventStatusForm({ open: false })}
        />
      </Modal>
    )
  }

  return <Cont>{content}</Cont>
}

export default SettingsModal
